import {
  Brain,
  Zap,
  CloudRain,
  BatteryLow,
  UserX,
  EyeOff,
  Users,
  Shield,
  History,
  Flame,
  Hourglass,
  Compass,
  Waves,
  Sunrise,
  AlertOctagon,
} from "lucide-react";
import { useReveal } from "../../hooks/useReveal";

const SUITABLE = [
  { icon: Brain, label: "Constant worry · racing thoughts" },
  { icon: Zap, label: "Panic or dread out of nowhere" },
  { icon: CloudRain, label: "Low mood · loss of interest" },
  { icon: BatteryLow, label: "Exhaustion sleep doesn't fix" },
  { icon: UserX, label: "Harsh self-criticism" },
  { icon: EyeOff, label: "Feeling like a fraud despite success" },
  { icon: Users, label: "Same painful patterns in relationships" },
  { icon: Shield, label: "Difficulty setting boundaries" },
  { icon: History, label: "Weight of past experiences" },
  { icon: Flame, label: "Reactions that feel \u201Ctoo big\u201D" },
  { icon: Hourglass, label: "Burnout · carrying too much" },
  { icon: Compass, label: "Feeling stuck or directionless" },
  { icon: Waves, label: "At the mercy of emotions, or numb" },
  { icon: Sunrise, label: "A major life transition" },
];

const NOT_SUITABLE = [
  "Active suicidal intent or recent suicide attempts",
  "Current self-harm requiring crisis or risk management",
  "Significant risk to others",
  "Acute psychiatric crisis requiring intensive support",
  "Emergency, forensic, or specialist safeguarding needs",
];

export const WhoIWorkWith = () => {
  const ref = useReveal();
  return (
    <section
      id="who"
      data-testid="who-section"
      className="bg-[#F3F0EA]"
    >
      <div className="mx-auto max-w-[1320px] px-6 md:px-10 lg:px-16 py-20 md:py-20 lg:py-24">
        <div ref={ref} className="reveal">
          <div className="mb-12 md:mb-16 max-w-3xl">
            <p className="eyebrow-rule text-[11px] uppercase tracking-[0.28em] text-[#4A4D4A] mb-5">
              <span>05 · Who I Work With</span>
            </p>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-[3rem] leading-[1.1] tracking-[-0.02em] text-[#1A1C1A] font-medium">
              An adult practice &mdash; with{" "}
              <em className="italic font-normal text-[#1B2E26]">clear limits</em>.
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
            {/* Suitable — icon chip grid (wide) */}
            <div
              data-testid="suitable-card"
              className="lg:col-span-8 bg-[#FDFBF7] border border-[#DCD7CE] p-7 md:p-10"
            >
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#1B2E26]">
                Suitable for adults experiencing
              </p>
              <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {SUITABLE.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.label}
                      className="flex items-center gap-3 border border-[#DCD7CE] hover:border-[#1B2E26] hover:bg-[#F3F0EA]/60 transition-all px-4 py-3"
                    >
                      <Icon
                        size={16}
                        strokeWidth={1.5}
                        className="text-[#1B2E26] shrink-0"
                      />
                      <span className="text-sm text-[#1A1C1A] leading-snug">
                        {item.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Not suitable (narrow) */}
            <div
              data-testid="not-suitable-card"
              className="lg:col-span-4 bg-[#FDFBF7] border border-[#DCD7CE] p-7 md:p-10"
            >
              <div className="flex items-center gap-3 text-[#7A3E2A]">
                <AlertOctagon size={16} strokeWidth={1.6} />
                <p className="text-[10px] uppercase tracking-[0.24em]">
                  Not suitable for
                </p>
              </div>
              <p className="mt-3 font-serif-display text-lg md:text-xl text-[#1A1C1A] leading-snug">
                This practice does not offer crisis services.
              </p>
              <ul className="mt-6 space-y-3">
                {NOT_SUITABLE.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-[#4A4D4A] text-sm leading-relaxed"
                  >
                    <span
                      aria-hidden="true"
                      className="mt-2 inline-block w-3 h-px bg-[#7A3E2A] shrink-0"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#crisis"
                data-testid="who-crisis-link"
                className="mt-6 inline-block link-underline text-sm text-[#1B2E26]"
              >
                See crisis support &rarr;
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
